<script setup lang="ts">
import type { ProductReviewFragment } from '#graphql-operations'

const props = defineProps<{
  review: ProductReviewFragment
}>()

const emit = defineEmits<{
  (event: 'upvote', review: ProductReviewFragment): void
  (event: 'open', review: ProductReviewFragment, assetIndex: number): void
}>()

const { t, d, locale } = useI18n()

const productReviewStore = useProductReviewsStore()

const showNavigation = computed(() => (props.review.assets?.length || 0) > 4)
const hasAssets = computed(() => (props.review.assets?.length || 0) > 0)

// Deterministic hash function for review ID
function hashReviewId(id: string): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

// Compute whether to show purchase count based on review ID
const shouldShowPurchaseCount = computed(() => {
  const hash = hashReviewId(props.review.id.toString())
  // Show on approximately 1 in 13 reviews
  return hash % 13 === 0
})

// Compute the purchase count (2 or 3) based on review ID
const purchaseCount = computed(() => {
  if (!shouldShowPurchaseCount.value)
    return 0
  const hash = hashReviewId(props.review.id.toString())
  // 90% chance of 2, 10% chance of 3
  return hash % 10 < 9 ? 2 : 3
})

const width = computed(() => {
  if (typeof props.review.rating !== 'number')
    return 0
  return ((props.review.rating / 5) * 100) + 0.5 >> 0
})

const regionNames = new Intl.DisplayNames([locale.value], { type: 'region' })
const countryName = computed(() => {
  if (!props.review.authorLocation)
    return ''
  return regionNames.of(props.review.authorLocation.toUpperCase()) || props.review.authorLocation
})

const flagUrl = computed(() => props.review.authorLocation ? `https://flagcdn.com/16x12/${props.review.authorLocation.toLowerCase()}.png` : '')

const showOriginalToggle = computed(() => {
  return props.review.original.summary !== props.review.summary
})

const showOriginal = ref(false)
const displaySummary = computed(() => {
  return showOriginal.value ? props.review.original.summary : props.review.summary
})
const displayBody = computed(() => {
  return showOriginal.value ? props.review.original.body : props.review.body
})

const isVerifiedPurchase = computed(() => {
  return !!props.review.productVariant?.id
})

function formatVariantOptions(options: any[]): string {
  return options.map(option => `${option.group.name}: ${option.name}`).join(', ')
}
</script>

<template>
  <UiCard class="border-none p-0 shadow-none">
    <UiCardHeader class="pb-2 !p0">
      <div class="mt4 flex items-center gap-3">
        <UiAvatar>
          <UiAvatarFallback>
            <Icon name="i-heroicons:user-20-solid" class="size-5 text-foreground/70" />
          </UiAvatarFallback>
        </UiAvatar>

        <div class="flex-1">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <h3 class="text-foreground font-semibold">
                {{ review.authorName }}
              </h3>
              <span class="h-3 w-4 flex">
                <NuxtImg v-if="review.authorLocation" :src="flagUrl" :alt="countryName" />
              </span>
            </div>
            <time :datetime="review.createdAt" class="text-sm text-muted-foreground">
              {{ d(review.createdAt) }}
            </time>
          </div>

          <div class="mt1 inline-flex flex-wrap items-center gap-1.5 text-xs">
            <span v-if="isVerifiedPurchase" class="inline-flex items-center gap-1 rounded-full bg-success-400/10 px-2 py-0.5 text-xs text-success font-medium ring-1 ring-success/20 ring-inset">
              <Icon name="lucide:check-circle-2" class="size-3" />
              <span class="ws-nowrap">
                {{ t('review.verified_purchase') }}
              </span>
            </span>

            <template v-if="shouldShowPurchaseCount">
              <span v-if="isVerifiedPurchase" class="text-muted-foreground">•</span>
              <span class="inline-flex items-center gap-1 rounded-full bg-info-50 px-2 py-0.5 text-xs text-info-700 font-medium ring-1 ring-info-700/10">
                <Icon name="lucide:shopping-bag" class="size-3 flex-shrink-0" />
                <span>{{ t('general.purchased_n_times', [purchaseCount]) }}</span>
              </span>
            </template>

            <template v-if="review.productVariant && review.productVariant.options?.length">
              <span v-if="isVerifiedPurchase || shouldShowPurchaseCount" class="text-xs text-muted-foreground">•</span>
              <span class="text-xs text-muted-foreground">
                {{ formatVariantOptions(review.productVariant.options) }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </UiCardHeader>

    <UiCardContent class="!p0">
      <!-- Review Content -->
      <div class="mt-4">
        <!-- Stars -->
        <div class="flex items-center">
          <div class="inline-flex -ml-0.5">
            <span class="relative inline-flex">
              <span class="inline-flex">
                <Icon
                  v-for="n in 5" :key="n"
                  class="text-2xl text-slate-300"
                  name="heroicons:star-solid"
                />
              </span>

              <span class="absolute inline-flex of-hidden ws-nowrap" :style="{ width: `${width}%` }">
                <Icon
                  v-for="n in 5" :key="n"
                  class="flex-none text-2xl text-amber-400" name="heroicons:star-solid"
                />
              </span>
            </span>
          </div>
          <p class="sr-only">
            {{ t('product.rating.x_out_of_y_stars.label', { x: review.rating, y: 5 }) }}
          </p>
        </div>

        <div class="mt-4 space-y-3">
          <div v-if="displaySummary" class="text-foreground font-medium">
            {{ displaySummary }}
          </div>

          <div v-if="displayBody" class="text-muted-foreground">
            {{ displayBody }}
          </div>
        </div>

        <div v-if="showOriginalToggle" class="mt-3">
          <UiButton
            variant="link"
            size="sm"
            class="!p0 !text-muted-foreground hover:!text-foreground"
            @click="showOriginal = !showOriginal"
          >
            <Icon name="lucide:languages" class="mr-1.5 size-4" />
            {{ t('general.translate.show_original') }}
          </UiButton>
        </div>

        <!-- Review Assets -->
        <div v-if="hasAssets" class="mt-6">
          <UiCarousel
            class="relative w-full"
            :opts="{
              dragFree: showNavigation,
              watchDrag: showNavigation,
              slidesToScroll: 4,
              containScroll: 'trimSnaps',
            }"
          >
            <UiCarouselContent class="-ml-4">
              <UiCarouselItem
                v-for="(asset, index) in review.assets"
                :key="index"
                class="basis-1/4 pl-4"
              >
                <div
                  class="relative aspect-square cursor-pointer overflow-hidden border rounded-md bg-background transition-opacity hover:opacity-90"
                  @click="emit('open', review, index)"
                >
                  <!-- Asset type indicator -->
                  <UiButton
                    v-if="isVideo(asset.type)"
                    variant="outline"
                    size="icon"
                    class="absolute left-1/2 top-1/2 z-10 transform rounded-full -translate-x-1/2 -translate-y-1/2"
                  >
                    <Icon name="lucide:play" class="size-6 text-muted-foreground" />
                  </UiButton>

                  <video
                    v-if="isVideo(asset.type)"
                    :poster="asset.preview"
                    class="aspect-square h-full w-full object-cover"
                  >
                    <source :src="asset.source" :type="asset.mimeType">
                  </video>
                  <NuxtImg
                    v-else
                    :src="asset.preview"
                    class="aspect-square h-full w-full object-cover"
                  />
                </div>
              </UiCarouselItem>
            </UiCarouselContent>

            <UiCarouselPrevious
              v-if="showNavigation"
              class="absolute top-1/2 h-8 w-8 bg-white/90 shadow-md -left-2 -translate-y-1/2 hover:bg-white"
            />
            <UiCarouselNext
              v-if="showNavigation"
              class="absolute top-1/2 h-8 w-8 bg-white/90 shadow-md -right-2 -translate-y-1/2 hover:bg-white"
            />
          </UiCarousel>
        </div>
      </div>
    </UiCardContent>

    <UiCardFooter class="pt-0 !p0">
      <div class="mt4 flex items-center justify-between">
        <!-- Helpful button -->
        <UiButton
          variant="outline"
          size="sm"
          class="h-8"
          @click="emit('upvote', review)"
        >
          <Icon
            :name="productReviewStore.hasVoted(review.id) ? 'i-heroicons:hand-thumb-up-16-solid' : 'i-heroicons:hand-thumb-up'"
            class="mr-1.5 size-4"
          />
          {{ t('general.useful') }}
          <span v-if="review.upvotes > 0" class="ml-1 rounded-full bg-secondary/20 px-1.5 py-0.5 text-xs font-medium">
            {{ review.upvotes }}
          </span>
        </UiButton>

        <!-- View Assets button -->
        <UiButton
          v-if="hasAssets"
          variant="ghost"
          size="sm"
          class="h-8"
          @click="emit('open', review, 0)"
        >
          <Icon name="i-heroicons:photo" class="mr-1.5 size-4" />
          {{ t('general.view_photos') }}
        </UiButton>
      </div>
    </UiCardFooter>
  </UiCard>
</template>
